<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import BuildingRenderCanvasAccessible from '@/components/maps_canvases/BuildingRenderCanvasAccessible.vue';
import ArrowLeft from '@/components/svg/ArrowLeft.vue';
import ArrowRight from '@/components/svg/ArrowRight.vue';
import { useCanvas } from '@/composables/useCanvas';
import type { AvailableLocales, LocaleSchema } from '@/locales/index';
import type { BuildingFacade } from '@/typings/buildings/BuildingFacade';
import type { FacadeRenderLayer } from '@/typings/drawing/FacadeRenderLayer';
import type { Unit } from '@/typings/units/Unit';
import { getBackgroundImageUrl } from '@/utils/canvas';

interface Props {
  buildingFacades: BuildingFacade[];
  units: Unit[];
  selectedFacade?: BuildingFacade;
}

interface Emits {
  (e: 'facade-select', facade: BuildingFacade): void;
  (e: 'unit-layer-click', layer: FacadeRenderLayer): void;
  (e: 'unit-layer-hover', layer: FacadeRenderLayer | undefined): void;
  (e: 'canvas-loaded'): void;
  (e: 'update:selectedUnit', unit: Unit | undefined): void;
  (e: 'update:hoveredUnit', unit: Unit | undefined): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n<{ message: LocaleSchema }, AvailableLocales>({
  useScope: 'global',
});

const buildingCanvasRef = useTemplateRef<HTMLElement>('buildingCanvasRef');
const thumbnailsContainer = useTemplateRef<HTMLElement>('thumbnailsContainer');
const facadeCarouselRef = useTemplateRef<HTMLElement>('facadeCarouselRef');

const showLeftArrow = ref(false);
const showRightArrow = ref(false);
const { isNullOrEmpty } = useCanvas();

// Models pour la synchronisation bidirectionnelle
const selectedUnit = defineModel<Unit | undefined>('selectedUnit');
const hoveredUnit = defineModel<Unit | undefined>('hoveredUnit');

// Computed properties
const sortedFacades = computed(() => {
  return [...props.buildingFacades].sort((a, b) => a.facadeOrder - b.facadeOrder);
});

const currentFacadeLayers = computed<FacadeRenderLayer[]>(() => {
  if (!props.selectedFacade) return [];
  
  return props.selectedFacade.facadeUnits.map(facadeUnit => {
    const unit = props.units.find(u => u.id === facadeUnit.unitId);
    if (!unit || isNullOrEmpty(facadeUnit.canvasPolygonData)) return null;
    
    try {
      const polygons = JSON.parse(facadeUnit.canvasPolygonData!);
      return {
        id: unit.id,
        unitId: unit.id,
        unitNumber: unit.unitNumber,
        floorName: unit.floorNo,
        status: unit.status,
        basePrice: unit.basePrice,
        bedrooms: unit.bedrooms,
        bathrooms: unit.bathrooms,
        dens: unit.dens,
        polygons: polygons
      } as FacadeRenderLayer;
    } catch (error) {
      console.error('Error parsing polygon data for unit:', facadeUnit.unitId, error);
      return null;
    }
  }).filter(layer => layer !== null) as FacadeRenderLayer[];
});

const currentFacadeImageUrl = computed(() => {
  return props.selectedFacade ? props.selectedFacade.buildingImageUrl : '';
});

const hasMultipleFacades = computed(() => {
  return sortedFacades.value.length > 1;
});

// Functions
function onFacadeSelect(facade: BuildingFacade) {
  emit('facade-select', facade);
}

function goToPreviousFacade() {
  if (!hasMultipleFacades.value || !props.selectedFacade) return;
  
  const currentIndex = sortedFacades.value.findIndex(f => f.id === props.selectedFacade?.id);
  const newIndex = currentIndex === 0
    ? sortedFacades.value.length - 1
    : currentIndex - 1;
  
  emit('facade-select', sortedFacades.value[newIndex]);
}

function goToNextFacade() {
  if (!hasMultipleFacades.value || !props.selectedFacade) return;
  
  const currentIndex = sortedFacades.value.findIndex(f => f.id === props.selectedFacade?.id);
  const newIndex = currentIndex === sortedFacades.value.length - 1
    ? 0
    : currentIndex + 1;
  
  emit('facade-select', sortedFacades.value[newIndex]);
}

function handleKeyboardNavigation(event: KeyboardEvent) {
  if (!hasMultipleFacades.value) return;
  
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    goToPreviousFacade();
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    goToNextFacade();
  }
}

function scrollThumbnails(direction: 'left' | 'right') {
  const container = thumbnailsContainer.value;
  if (!container) return;
  
  const scrollAmount = 200; // Défiler 200px à la fois
  const currentScroll = container.scrollLeft;
  
  if (direction === 'left') {
    container.scrollTo({
      left: currentScroll - scrollAmount,
      behavior: 'smooth'
    });
  } else {
    container.scrollTo({
      left: currentScroll + scrollAmount,
      behavior: 'smooth'
    });
  }
}

function updateArrowVisibility() {
  const container = thumbnailsContainer.value;
  if (!container) return;
  
  const scrollLeft = container.scrollLeft;
  const scrollWidth = container.scrollWidth;
  const clientWidth = container.clientWidth;
  
  showLeftArrow.value = scrollLeft > 0;
  showRightArrow.value = scrollLeft < scrollWidth - clientWidth;
}

function scrollToSelectedThumbnail() {
  const container = thumbnailsContainer.value;
  if (!container || !props.selectedFacade) return;
  
  const selectedIndex = sortedFacades.value.findIndex(f => f.id === props.selectedFacade?.id);
  const selectedThumbnail = container.children[selectedIndex] as HTMLElement;
  
  if (!selectedThumbnail) return;
  
  const containerRect = container.getBoundingClientRect();
  const thumbnailRect = selectedThumbnail.getBoundingClientRect();
  
  // Calculer la position relative de la miniature par rapport au conteneur
  const thumbnailLeft = thumbnailRect.left - containerRect.left + container.scrollLeft;
  const thumbnailRight = thumbnailLeft + thumbnailRect.width;
  
  // Vérifier si la miniature est entièrement visible
  const isFullyVisible = thumbnailLeft >= container.scrollLeft &&
                         thumbnailRight <= container.scrollLeft + container.clientWidth;
  
  if (!isFullyVisible) {
    // Calculer la position de défilement optimale
    let scrollPosition;
    
    if (thumbnailLeft < container.scrollLeft) {
      // La miniature est à gauche de la zone visible, défiler vers la gauche
      scrollPosition = thumbnailLeft - 20; // Ajouter un petit padding
    } else {
      // La miniature est à droite de la zone visible, défiler vers la droite
      scrollPosition = thumbnailRight - container.clientWidth + 20; // Ajouter un petit padding
    }
    
    container.scrollTo({
      left: Math.max(0, scrollPosition),
      behavior: 'smooth'
    });
  }
}

function onUnitLayerClick(layer: FacadeRenderLayer) {
  const unit = props.units.find(u => u.id === layer.unitId);
  if (unit) {
    selectedUnit.value = unit;
  }
  emit('unit-layer-click', layer);
}

function onUnitLayerHover(layer: FacadeRenderLayer | undefined) {
  if (!layer) {
    hoveredUnit.value = undefined;
    emit('unit-layer-hover', undefined);
    return;
  }
  
  const unit = props.units.find(u => u.id === layer.unitId);
  if (unit) {
    hoveredUnit.value = unit;
  }
  emit('unit-layer-hover', layer);
}

function onBuildingCanvasLoaded() {
  emit('canvas-loaded');
}

// Watchers
watch(() => props.buildingFacades, () => {
  setTimeout(updateArrowVisibility, 100);
}, { deep: true });

watch(() => props.selectedFacade, () => {
  setTimeout(scrollToSelectedThumbnail, 100);
});

// Lifecycle
onMounted(() => {
  // Initialiser la visibilité des flèches après le chargement des données
  setTimeout(() => {
    updateArrowVisibility();
    scrollToSelectedThumbnail();
  }, 100);
});
</script>

<template>
  <div v-if="buildingFacades?.length"
       ref="facadeCarouselRef"
       class="facade-carousel"
       tabindex="0"
       @keydown="handleKeyboardNavigation">
    <!-- Façade principale -->
    <div class="facade-main">
      <div v-if="selectedFacade" class="facade-main-content">
        <div ref="buildingCanvasRef"
             class="facade-main-image">
          <BuildingRenderCanvasAccessible v-if="selectedFacade"
                                          :layers="currentFacadeLayers"
                                          :image-url="currentFacadeImageUrl"
                                          @canvas-loaded="onBuildingCanvasLoaded"
                                          @layer-click="onUnitLayerClick"
                                          @layer-hover="onUnitLayerHover">
            <template #tooltip="{ layer }">
              <div v-if="layer && layer.unitNumber">
                {{ `${t('browsingApp.units.unitNumber')} ${layer.unitNumber}` }}
              </div>
            </template>
          </BuildingRenderCanvasAccessible>
          <div v-else
               class="facade-canvas-empty">
            {{ t('browsingApp.buildings.noFacadeCanvasData') }}
          </div>
          
          <!-- Flèches de navigation sur l'image principale -->
          <template v-if="hasMultipleFacades && selectedFacade">
            <button class="facade-main-arrow facade-main-arrow--left"
                    @click="goToPreviousFacade"
                    :aria-label="t('common.previous')">
              <ArrowLeft class="facade-main-arrowIcon" />
            </button>
            <button class="facade-main-arrow facade-main-arrow--right"
                    @click="goToNextFacade"
                    :aria-label="t('common.next')">
              <ArrowRight class="facade-main-arrowIcon" />
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- Miniatures des façades -->
    <div v-if="sortedFacades.length > 1" class="facade-thumbnails-container">
      <button v-if="showLeftArrow"
              class="facade-thumbnails-arrow facade-thumbnails-arrow--left"
              @click="scrollThumbnails('left')">
        <ArrowLeft class="facade-arrow-icon" />
      </button>
      
      <div ref="thumbnailsContainer"
           class="facade-thumbnails"
           @scroll="updateArrowVisibility">
        <button v-for="facade in sortedFacades"
                :key="facade.id"
                class="facade-thumbnail"
                :class="{ 'facade-thumbnail--active': selectedFacade?.id === facade.id }"
                @click="onFacadeSelect(facade)">
          <img v-if="facade.buildingImageUrl"
               :src="getBackgroundImageUrl(facade.buildingImageUrl)"
               :alt="facade.facadeName || t('browsingApp.facades.facadeNumber', { number: facade.facadeOrder })"
               class="facade-thumbnail-image" />
          <div v-else class="facade-thumbnail-placeholder">
            {{ facade.facadeOrder }}
          </div>
        </button>
      </div>
      
      <button v-if="showRightArrow"
              class="facade-thumbnails-arrow facade-thumbnails-arrow--right"
              @click="scrollThumbnails('right')">
        <ArrowRight class="facade-arrow-icon" />
      </button>
    </div>
  </div>


  <div v-else
       class="placeholderNoInformation">
    {{ t('browsingApp.buildings.noBuildingCanvasData') }}
  </div>
</template>

<style lang="scss" scoped>
/* Facade carousel styles */
.facade-carousel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  min-width: 0;
  outline: none; /* Supprime le contour par défaut */
  position: relative;
}

.facade-main {
  border-radius: 8px;
  background: var(--gray-50);
}

.facade-main-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.facade-main-image {
  flex: 1;
  position: relative;
}

.facade-canvas-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 300px;
  background: var(--gray-100);
  border: 2px dashed var(--gray-300);
  border-radius: 8px;
  color: var(--gray-500);
  font-size: 1.25rem;
  font-style: italic;
}

.facade-main-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid var(--gray-200);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.05);
    border-color: var(--primary-400);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }

  &--left {
    left: 12px;
  }

  &--right {
    right: 12px;
  }
}

.facade-main-arrowIcon {
  width: 20px;
  height: 20px;
  color: var(--gray-600);
  display:block;
  
  .facade-main-arrow:hover & {
    color: var(--primary-600);
  }
}

.facade-thumbnails-container {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--gray-50);
  border-radius: 8px;
  min-height: 100px;
  width: 100%;
  border: 1px solid var(--gray-200);
}

.facade-thumbnails-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid var(--gray-200);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
}

.facade-thumbnails-arrow--left {
  left: 8px;
}

.facade-thumbnails-arrow--right {
  right: 8px;
}

.facade-thumbnails-arrow:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-50%) scale(1.05);
  border-color: var(--primary-400);
}

.facade-arrow-icon {
  width: 20px;
  height: 20px;
  color: var(--gray-600);
}

.facade-thumbnails-arrow:hover .facade-arrow-icon {
  color: var(--primary-600);
}

.facade-thumbnails {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.facade-thumbnails::-webkit-scrollbar {
  display: none;
}

.facade-thumbnail {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: white;
  border: 2px solid var(--gray-200);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  max-width: 120px;
}

.facade-thumbnail:hover {
  border-color: var(--primary-400);
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.facade-thumbnail--active {
  border-color: var(--primary-500);
  background: var(--primary-50);
}

.facade-thumbnail-image {
  max-width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid var(--gray-200);
}

.facade-thumbnail-placeholder {
  width: 60px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gray-100);
  border: 1px dashed var(--gray-300);
  border-radius: 4px;
  color: var(--gray-500);
  font-size: 0.875rem;
  font-weight: 600;
}
</style>